import type { FunnelStep, ReportSource, ReportStart } from '../../../api/client.ts';
import { customEventIssues, type ActionBasedIssue } from '../../campaigns/trigger/actionBased.ts';
import { periodDays, periodProblem, type Period } from '../period.ts';

/** Contract limits (`funnelRetention`): steps, window, entry period, retention days. */
export const FUNNEL_MAX_STEPS = 6;
export const FUNNEL_MAX_WINDOW_DAYS = 30;
export const FUNNEL_DEFAULT_WINDOW_DAYS = 3;
export const REPORT_MAX_PERIOD_DAYS = 180;
export const RETENTION_DAY_OPTIONS = [1, 3, 7, 14, 30, 60, 90] as const;
export const RETENTION_DEFAULT_DAYS = [1, 3, 7, 14, 30];
export const RETENTION_MAX_DAYS = 10;

export const START_LABELS: Record<ReportStart, string> = {
  received: 'Received the message',
  accepted_by_provider: 'Push accepted by provider',
  card_impression: 'Card impression',
  iam_impression: 'In-app impression',
  canvas_entered: 'Entered the Canvas',
};

type FactType = Extract<FunnelStep, { kind: 'fact' }>['type'];
/** Message facts a step can be, by the channel they belong to (labels as the service names them). */
export const FACT_STEPS: ReadonlyArray<{ channel: 'push' | 'content_card' | 'in_app'; group: string; items: ReadonlyArray<{ type: FactType; label: string }> }> = [
  {
    channel: 'push',
    group: 'Push',
    items: [
      { type: 'delivered', label: 'Received on device' },
      { type: 'push_open_direct', label: 'Push direct open' },
      { type: 'push_open_influenced', label: 'Push influenced open' },
      { type: 'push_button_click', label: 'Push button click' },
    ],
  },
  {
    channel: 'content_card',
    group: 'Content Card',
    items: [
      { type: 'card_impression', label: 'Card impression' },
      { type: 'card_click', label: 'Card click' },
      { type: 'card_dismiss', label: 'Card dismiss' },
    ],
  },
  {
    channel: 'in_app',
    group: 'In-app message',
    items: [
      { type: 'iam_impression', label: 'In-app impression' },
      { type: 'iam_click', label: 'In-app click' },
      { type: 'iam_close', label: 'In-app close' },
      { type: 'iam_form_submit', label: 'In-app form submit' },
    ],
  },
];

/** Starts a source can have: a campaign starts at a receipt, a Canvas also at its entry. */
export function startsFor(source: ReportSource, channels?: readonly string[]): ReportStart[] {
  const has = (channel: string) => !channels || channels.includes(channel);
  const receipts: ReportStart[] = ['received', ...(has('push') ? (['accepted_by_provider'] as const) : []), ...(has('content_card') ? (['card_impression'] as const) : []), ...(has('in_app') ? (['iam_impression'] as const) : [])];
  return 'canvasId' in source ? ['canvas_entered', ...receipts] : receipts;
}

/** Fact groups for the campaign's channels; a Canvas (no channel list) gets them all. */
export const factStepsFor = (channels?: readonly string[]) => FACT_STEPS.filter((g) => !channels || channels.includes(g.channel));

/** Why the reports would refuse the period, or null. */
export function reportPeriodProblem(period: Period): string | null {
  const problem = periodProblem(period);
  if (problem) return problem;
  const days = periodDays(period)!;
  return days - 1 >= REPORT_MAX_PERIOD_DAYS ? `Funnel and retention reports cover less than ${REPORT_MAX_PERIOD_DAYS} days.` : null;
}

/** Event and operand issues of a custom event (`path` + `event` / `propertyFilters.<j>.<field>`), as the trigger editor shows them. */
export const eventIssues = customEventIssues;

/** Issues of the funnel steps (`steps.<i>…`): an event step needs an event and valid property filters. */
export const funnelIssues = (steps: readonly FunnelStep[]): ActionBasedIssue[] => steps.flatMap((s, i) => (s.kind === 'event' ? eventIssues(['steps', i], s.name, s.propertyFilters) : []));

/** `4 min`, `2 h 5 min`, `3 d 4 h` for a median time between steps. */
export function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days} d ${hours % 24} h` : `${days} d`;
}

export const pct = (value: number | null) => (value === null ? '—' : `${(value * 100).toFixed(1)} %`);
